import React, { useEffect, useRef, useState } from 'react';

const MONTHS = ['januar', 'februar', 'marts', 'april', 'maj', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'december'];
const WEEKDAYS = ['ma', 'ti', 'on', 'to', 'fr', 'lø', 'sø'];

const pad = (n) => String(n).padStart(2, '0');
const toIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function parseIso(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return y && m && d ? new Date(y, m - 1, d) : null;
}

function formatDisplay(iso) {
  const d = parseIso(iso);
  return d ? `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}` : '';
}

const Chevron = ({ dir }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={dir === 'left' ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'} />
  </svg>
);

// Our own calendar popover instead of the browser's native one, which can't
// be styled and showed a second, duplicate calendar icon inside the field.
// Value is an ISO date string (yyyy-mm-dd), same as <input type="date">.
// `min` / `max` (ISO dates) grey out the days outside the allowed range; `allowClear={false}` hides "Ryd" for a date that must stay set.
export default function DatePicker({ value, onChange, placeholder = 'Vælg dato', ariaLabel, min = null, max = null, allowClear = true }) {
  const [open, setOpen] = useState(false);
  const selected = parseIso(value);
  const today = new Date();
  const start = selected || today;
  const [view, setView] = useState({ y: start.getFullYear(), m: start.getMonth() });
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function onDown(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    function onScroll(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  function toggle() {
    if (!open) {
      const base = parseIso(value) || new Date();
      setView({ y: base.getFullYear(), m: base.getMonth() });
      // Fixed to the screen (not the scrolling panel body) so it's never
      // clipped; opens on whichever side of the field has more room.
      const r = rootRef.current.getBoundingClientRect();
      const width = 280;
      const height = 346;
      const above = r.top - 8;
      const below = window.innerHeight - r.bottom - 8;
      const top = above >= height || above >= below ? Math.max(8, r.top - height - 8) : r.bottom + 8;
      const left = Math.max(8, Math.min(r.right - width, window.innerWidth - width - 8));
      setPos({ top, left });
    }
    setOpen((o) => !o);
  }

  function shift(delta) {
    setView((v) => {
      const d = new Date(v.y, v.m + delta, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });
  }

  function pick(d) {
    onChange(toIso(d));
    setOpen(false);
  }

  const offset = (new Date(view.y, view.m, 1).getDay() + 6) % 7;
  const cells = Array.from({ length: 42 }, (_, i) => new Date(view.y, view.m, 1 - offset + i));
  const todayIso = toIso(today);
  const outOfRange = (iso) => (min && iso < min) || (max && iso > max);

  return (
    <div className="doc-field dp" ref={rootRef}>
      <span className="doc-field__ic" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="4" y="5" width="16" height="15" rx="3" />
          <path d="M8 3v4M16 3v4M4 10h16" />
        </svg>
      </span>
      <button type="button" className={`dp__trigger${value ? ' has-value' : ''}`} onClick={toggle} aria-haspopup="dialog" aria-expanded={open} aria-label={ariaLabel || placeholder}>
        {value ? formatDisplay(value) : placeholder}
      </button>
      {open && (
        <div className="dp__pop" role="dialog" aria-label="Vælg dato" style={{ top: pos.top, left: pos.left }}>
          <div className="dp__head">
            <span className="dp__title">{MONTHS[view.m]} {view.y}</span>
            <span className="dp__nav">
              <button type="button" onClick={() => shift(-1)} aria-label="Forrige måned"><Chevron dir="left" /></button>
              <button type="button" onClick={() => shift(1)} aria-label="Næste måned"><Chevron dir="right" /></button>
            </span>
          </div>
          <div className="dp__grid dp__weekdays">
            {WEEKDAYS.map((w) => <span key={w}>{w}</span>)}
          </div>
          <div className="dp__grid">
            {cells.map((d) => {
              const iso = toIso(d);
              const cls = ['dp__day'];
              if (d.getMonth() !== view.m) cls.push('is-out');
              if (iso === todayIso) cls.push('is-today');
              if (value && iso === value) cls.push('is-selected');
              return (
                <button type="button" key={iso} className={cls.join(' ')} onClick={() => pick(d)} disabled={outOfRange(iso)}>
                  {d.getDate()}
                </button>
              );
            })}
          </div>
          <div className="dp__foot">
            {allowClear ? <button type="button" onClick={() => { onChange(''); setOpen(false); }}>Ryd</button> : <span />}
            <button type="button" onClick={() => pick(new Date())} disabled={outOfRange(todayIso)}>I dag</button>
          </div>
        </div>
      )}
    </div>
  );
}
